import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getConfiguracionRecordatorioCuotas,
  guardarConfiguracionRecordatorioCuotas,
} from '../../../services/cobranzaService';

const ejemploProxima = (texto) => (texto || '')
  .replace(/\{nombre\}/g, 'Juan')
  .replace(/\{numero\}/g, '3')
  .replace(/\{monto\}/g, '$150.000')
  .replace(/\{vencimiento\}/g, '15/10/2026');

const ejemploVencida = (texto) => (texto || '')
  .replace(/\{nombre\}/g, 'Juan')
  .replace(/\{cuotas\}/g, 'cuotas 3 y 4')
  .replace(/\{monto\}/g, '$300.000')
  .replace(/\{desde\}/g, '15/09/2026');

/**
 * Recordatorios de cuota (V263): cuántos días antes del vencimiento sale el automático y los dos
 * textos (el automático y el de cuota vencida, que se manda a mano desde Deudores). Se guardan en
 * el sistema, como las plantillas de citación.
 */
export default function PlantillasRecordatorioCuota() {
  const [config, setConfig] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    getConfiguracionRecordatorioCuotas()
      .then(setConfig)
      .catch(() => setConfig({ diasAntes: 2, plantillaProxima: '', plantillaVencida: '' }));
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      setConfig(await guardarConfiguracionRecordatorioCuotas({ ...config, diasAntes: Number(config.diasAntes) }));
      toast.success('Recordatorios de cuota guardados.');
    } catch (err) {
      toast.error(err.response?.status === 403
        ? 'No tenés permiso para cambiar los recordatorios.'
        : (err.response?.data?.message || 'No se pudieron guardar.'));
    } finally {
      setGuardando(false);
    }
  };

  if (!config) return null;
  const dias = Number(config.diasAntes);
  return (
    <div className="card shadow-sm border-0 rounded-4 p-4" style={{ gridColumn: '1 / -1' }}>
      <h5 className="fw-semibold mb-1"><i className="bi bi-cash-coin me-2" />Recordatorio de cuotas</h5>
      <p className="text-muted small mb-3">
        Un solo mensaje automático por cuota, antes del vencimiento. No sale si la cuota ya está pagada.
        Todo lo que se manda queda en la bitácora del cobro.
      </p>

      <div className="d-flex align-items-center gap-2 mb-3">
        <label htmlFor="dias-antes-cuota" className="form-label fw-semibold mb-0">Mandarlo</label>
        <input
          id="dias-antes-cuota" type="number" min={0} max={15} className="form-control" style={{ width: 80 }}
          value={config.diasAntes ?? ''} onChange={(e) => setConfig({ ...config, diasAntes: e.target.value })}
        />
        <span>{dias === 0 ? 'días antes (0 = el mismo día del vencimiento)' : 'días antes del vencimiento'}</span>
      </div>
      <p className="small text-muted mt-n2 mb-3">Si ese día cae sábado o domingo, sale el viernes.</p>

      <div className="mb-3">
        <label className="form-label fw-semibold" htmlFor="plantilla-cuota-proxima">Automático, antes del vencimiento</label>
        <p className="text-muted small mb-1">
          Variables: <code className="me-2">{'{nombre}'}</code><code className="me-2">{'{numero}'}</code>
          <code className="me-2">{'{monto}'}</code><code>{'{vencimiento}'}</code>
        </p>
        <textarea
          id="plantilla-cuota-proxima" className="form-control" rows={4} maxLength={1000}
          value={config.plantillaProxima || ''} onChange={(e) => setConfig({ ...config, plantillaProxima: e.target.value })}
        />
        <div className="small mt-1" style={{ color: '#374151', whiteSpace: 'pre-line' }}>
          <strong>Ejemplo:</strong> {ejemploProxima(config.plantillaProxima)}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold" htmlFor="plantilla-cuota-vencida">Cuota vencida (botón "Recordar" de Deudores)</label>
        <p className="text-muted small mb-1">
          Variables: <code className="me-2">{'{nombre}'}</code><code className="me-2">{'{cuotas}'}</code>
          <code className="me-2">{'{monto}'}</code><code>{'{desde}'}</code>
        </p>
        <textarea
          id="plantilla-cuota-vencida" className="form-control" rows={4} maxLength={1000}
          value={config.plantillaVencida || ''} onChange={(e) => setConfig({ ...config, plantillaVencida: e.target.value })}
        />
        <div className="small mt-1" style={{ color: '#374151', whiteSpace: 'pre-line' }}>
          <strong>Ejemplo:</strong> {ejemploVencida(config.plantillaVencida)}
        </div>
      </div>

      <div>
        <button
          type="button" className="btn btn-primary" onClick={guardar}
          disabled={guardando || Number.isNaN(dias) || dias < 0 || dias > 15}
        >
          {guardando ? 'Guardando…' : 'Guardar recordatorios de cuota'}
        </button>
      </div>
    </div>
  );
}
